import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { LoaderCircle, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import actionItemService from '../../services/actionItemService';
import centresService from '../../services/centresService';
import './ActionItems.css';

const initialForm = {
  formation: '',
  gap_problem_detected: '',
  corrective_action: '',
  accountable_user: '',
  comment: '',
  due_date: '',
  cost: '',
  currency: '',
  centre: '',
  approval_mode: 'ONE',
  manager_ids: [],
};

const normalizeResults = (response) => (
  Array.isArray(response) ? response : response?.results || []
);

const ActionItemCreateModal = ({
  open,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const optionsRequestRef = useRef(0);
  const [form, setForm] = useState(initialForm);
  const [centres, setCentres] = useState([]);
  const [formations, setFormations] = useState([]);
  const [managers, setManagers] = useState([]);
  const [accountables, setAccountables] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const loadCentreOptions = useCallback(async (
    centreId,
    availableCentres,
  ) => {
    const requestId = optionsRequestRef.current + 1;
    optionsRequestRef.current = requestId;
    const selected = availableCentres.find(
      (item) => String(item.id) === centreId,
    );

    setForm((current) => ({
      ...current,
      centre: centreId,
      formation: '',
      accountable_user: user?.id ? String(user.id) : '',
      manager_ids: [],
      approval_mode: 'ONE',
      currency: '',
    }));
    setFormations([]);
    setManagers([]);
    setAccountables([]);
    setCurrencies([]);
    setError('');
    if (!centreId) return;

    setLoadingOptions(true);
    try {
      const options = await actionItemService.formOptions(centreId);
      if (requestId !== optionsRequestRef.current) return;
      setFormations(options.formations || []);
      setManagers(options.managers || []);
      setAccountables(options.accountables || []);
      const nextCurrencies = options.currencies?.length
        ? options.currencies
        : [options.currency || selected?.devise].filter(Boolean);
      const defaultCurrency = (
        options.currency && nextCurrencies.includes(options.currency)
          ? options.currency
          : nextCurrencies[0] || ''
      );
      setCurrencies(nextCurrencies);
      setForm((current) => ({
        ...current,
        currency: defaultCurrency,
      }));
    } catch (requestError) {
      if (requestId !== optionsRequestRef.current) return;
      setError(t('actionItems.errors.options'));
    } finally {
      if (requestId === optionsRequestRef.current) {
        setLoadingOptions(false);
      }
    }
  }, [t, user?.id]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;

    const loadCentres = async () => {
      setLoading(true);
      setError('');
      setCentres([]);
      setFormations([]);
      setManagers([]);
      setAccountables([]);
      setCurrencies([]);
      setForm({
        ...initialForm,
        accountable_user: user?.id ? String(user.id) : '',
      });
      try {
        const response = await centresService.getAll();
        if (!active) return;
        const availableCentres = normalizeResults(response);
        setCentres(availableCentres);
        if (availableCentres.length === 1) {
          await loadCentreOptions(
            String(availableCentres[0].id),
            availableCentres,
          );
        }
      } catch (requestError) {
        if (active) setError(t('actionItems.errors.centres'));
      } finally {
        if (active) setLoading(false);
      }
    };

    loadCentres();
    return () => {
      active = false;
      optionsRequestRef.current += 1;
    };
  }, [loadCentreOptions, open, t, user?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, open, submitting]);

  const updatePrimaryManager = (managerId) => {
    setForm((current) => {
      if (!managerId) {
        return {
          ...current,
          manager_ids: [],
          approval_mode: 'ONE',
        };
      }
      const secondary = current.manager_ids[1] || '';
      const managerIds = [
        managerId,
        secondary === managerId ? '' : secondary,
      ].filter(Boolean);
      return {
        ...current,
        manager_ids: managerIds,
        approval_mode: managerIds.length === 2
          ? current.approval_mode
          : 'ONE',
      };
    });
  };

  const updateSecondaryManager = (managerId) => {
    setForm((current) => {
      const primary = current.manager_ids[0] || '';
      const managerIds = [primary, managerId].filter(Boolean);
      return {
        ...current,
        manager_ids: managerIds,
        approval_mode: managerIds.length === 2
          ? current.approval_mode
          : 'ONE',
      };
    });
  };

  const isValid = Boolean(
    form.centre
      && form.formation
      && form.gap_problem_detected.trim()
      && form.corrective_action.trim()
      && form.manager_ids.length
      && (!form.cost || form.currency),
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isValid || submitting) return;

    setSubmitting(true);
    setError('');
    try {
      const item = await actionItemService.create({
        formation: form.formation,
        gap_problem_detected: form.gap_problem_detected.trim(),
        corrective_action: form.corrective_action.trim(),
        accountable_user: form.accountable_user || String(user.id),
        comment: form.comment.trim(),
        due_date: form.due_date || null,
        cost: form.cost || null,
        currency: form.currency,
        centre: form.centre,
        approval_mode: form.manager_ids.length === 1
          ? 'ONE'
          : form.approval_mode,
        manager_ids: form.manager_ids,
      });
      toast.success(t('actionItems.created'));
      onCreated(item);
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail
          || t('actionItems.errors.create'),
      );
      toast.error(t('actionItems.errors.create'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="action-item-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
    >
      <section
        className="action-item-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="action-item-modal-title"
      >
        <header className="action-item-modal-header">
          <div>
            <h2 id="action-item-modal-title">{t('actionItems.new')}</h2>
            <p>{t('actionItems.createSubtitle')}</p>
          </div>
          <button
            type="button"
            className="action-item-modal-close"
            aria-label={t('actionItems.closeCreateModal')}
            disabled={submitting}
            onClick={onClose}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        {error && (
          <div className="action-items-alert action-item-modal-alert" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="action-items-state">
            <LoaderCircle size={18} aria-hidden="true" />
            {t('common.loading')}
          </div>
        ) : (
          <form className="action-item-form" onSubmit={handleSubmit}>
            <section className="action-item-form-section">
              <h3>{t('actionItems.form.details')}</h3>
              <div className="action-item-form-grid">
                <label className="action-item-field">
                  <span>{t('actionItems.trainingCourse')} *</span>
                  <select
                    value={form.formation}
                    disabled={!form.centre || loadingOptions}
                    onChange={(event) => (
                      updateField('formation', event.target.value)
                    )}
                    required
                  >
                    <option value="">
                      {t('actionItems.selectFormation')}
                    </option>
                    {formations.map((formationOption) => (
                      <option
                        key={formationOption.id}
                        value={formationOption.id}
                      >
                        {formationOption.title}
                      </option>
                    ))}
                  </select>
                  {!loadingOptions && form.centre
                    && formations.length === 0 && (
                    <small>{t('actionItems.noFormations')}</small>
                  )}
                </label>

                <label className="action-item-field">
                  <span>{t('actionItems.location')} *</span>
                  <select
                    value={form.centre}
                    onChange={(event) => loadCentreOptions(
                      event.target.value,
                      centres,
                    )}
                    required
                  >
                    <option value="">
                      {t('actionItems.selectCentre')}
                    </option>
                    {centres.map((centreOption) => (
                      <option
                        key={centreOption.id}
                        value={centreOption.id}
                      >
                        {centreOption.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="action-item-field action-item-field--wide">
                  <span>{t('actionItems.gapProblemDetected')} *</span>
                  <textarea
                    rows="3"
                    value={form.gap_problem_detected}
                    onChange={(event) => updateField(
                      'gap_problem_detected',
                      event.target.value,
                    )}
                    required
                  />
                </label>

                <label className="action-item-field action-item-field--wide">
                  <span>{t('actionItems.correctiveAction')} *</span>
                  <textarea
                    rows="4"
                    value={form.corrective_action}
                    onChange={(event) => updateField(
                      'corrective_action',
                      event.target.value,
                    )}
                    required
                  />
                </label>

                {isAdmin ? (
                  <label className="action-item-field">
                    <span>{t('actionItems.accountable')}</span>
                    <select
                      value={form.accountable_user}
                      onChange={(event) => updateField(
                        'accountable_user',
                        event.target.value,
                      )}
                    >
                      <option value={user.id}>
                        {t('actionItems.accountableSelf')}
                      </option>
                      {accountables.map((accountable) => (
                        <option
                          key={accountable.id}
                          value={accountable.id}
                        >
                          {accountable.first_name} {accountable.last_name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <label className="action-item-field">
                    <span>{t('actionItems.accountable')}</span>
                    <input
                      value={`${user?.first_name || ''} ${
                        user?.last_name || ''
                      }`.trim()}
                      disabled
                      readOnly
                    />
                  </label>
                )}

                <label className="action-item-field action-item-field--wide">
                  <span>{t('actionItems.commentFeedback')}</span>
                  <textarea
                    rows="3"
                    value={form.comment}
                    onChange={(event) => (
                      updateField('comment', event.target.value)
                    )}
                  />
                </label>

                <label className="action-item-field">
                  <span>{t('actionItems.dueDate')}</span>
                  <input
                    type="date"
                    value={form.due_date}
                    onChange={(event) => (
                      updateField('due_date', event.target.value)
                    )}
                  />
                </label>
              </div>
            </section>

            <section className="action-item-form-section">
              <h3>{t('actionItems.form.assignment')}</h3>
              {loadingOptions && (
                <div className="action-items-inline-loading">
                  <LoaderCircle size={17} aria-hidden="true" />
                  {t('actionItems.loadingOptions')}
                </div>
              )}
              {!loadingOptions && form.centre && managers.length === 0 && (
                <p>{t('actionItems.noManagers')}</p>
              )}
              <div className="action-item-manager-selects">
                <label className="action-item-field">
                  <span>
                    {t('actionItems.approvalRequiredBy1')} *
                  </span>
                  <select
                    value={form.manager_ids[0] || ''}
                    disabled={!form.centre || loadingOptions}
                    onChange={(event) => (
                      updatePrimaryManager(event.target.value)
                    )}
                    required
                  >
                    <option value="">
                      {t('actionItems.selectApprover')}
                    </option>
                    {managers.map((manager) => (
                      <option key={manager.id} value={manager.id}>
                        {manager.first_name} {manager.last_name}
                        {' — '}
                        {manager.email}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="action-item-field">
                  <span>{t('actionItems.approvalRequiredBy2')}</span>
                  <select
                    value={form.manager_ids[1] || ''}
                    disabled={
                      !form.centre
                      || loadingOptions
                      || !form.manager_ids[0]
                    }
                    onChange={(event) => (
                      updateSecondaryManager(event.target.value)
                    )}
                  >
                    <option value="">
                      {t('actionItems.selectApprover')}
                    </option>
                    {managers
                      .filter((manager) => (
                        String(manager.id) !== form.manager_ids[0]
                      ))
                      .map((manager) => (
                        <option key={manager.id} value={manager.id}>
                          {manager.first_name} {manager.last_name}
                          {' — '}
                          {manager.email}
                        </option>
                      ))}
                  </select>
                </label>
              </div>
              <label className="action-item-field">
                <span>{t('actionItems.approvalMode')}</span>
                <select
                  value={form.approval_mode}
                  onChange={(event) => updateField(
                    'approval_mode',
                    event.target.value,
                  )}
                >
                  <option value="ONE">
                    {t('actionItems.oneApproval')}
                  </option>
                  {form.manager_ids.length === 2 && (
                    <option value="ALL">
                      {t('actionItems.allApprovals')}
                    </option>
                  )}
                </select>
              </label>
            </section>

            <section className="action-item-form-section">
              <h3>{t('actionItems.form.cost')}</h3>
              <div className="action-item-form-grid">
                <label className="action-item-field">
                  <span>{t('actionItems.costExclTax')}</span>
                  <input
                    min="0"
                    step="0.01"
                    type="number"
                    value={form.cost}
                    onChange={(event) => (
                      updateField('cost', event.target.value)
                    )}
                  />
                </label>
                <label className="action-item-field">
                  <span>{t('actionItems.currency')}</span>
                  <select
                    value={form.currency}
                    disabled={
                      !form.centre
                      || loadingOptions
                      || currencies.length === 0
                    }
                    onChange={(event) => (
                      updateField('currency', event.target.value)
                    )}
                  >
                    {currencies.map((currencyOption) => (
                      <option key={currencyOption} value={currencyOption}>
                        {currencyOption}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="action-item-field">
                  <span>{t('actionItems.filters.status')}</span>
                  <input
                    value={t('actionItems.status.PENDING_APPROVAL')}
                    disabled
                    readOnly
                  />
                </label>
              </div>
            </section>

            <footer className="action-item-form-actions">
              <button
                className="action-items-secondary-button"
                type="button"
                disabled={submitting}
                onClick={onClose}
              >
                {t('common.cancel')}
              </button>
              <button
                className="action-items-primary-button"
                type="submit"
                disabled={!isValid || submitting}
              >
                {submitting
                  ? t('common.saving')
                  : t('actionItems.createSubmit')}
              </button>
            </footer>
          </form>
        )}
      </section>
    </div>
  );
};

export default ActionItemCreateModal;
